import React from 'react';
import { TrendingUp } from 'lucide-react';
import ProfileAvatar from '../shared/ProfileAvatar';
import { firstNameOf, greetingFor, momentumFor } from '../studentHomeRules';

const greetingLine = (name) => `${greetingFor(new Date().getHours())}, ${firstNameOf(name)}!`;

/**
 * Up to a laptop: the banner that opens the page. It carries the profile, because
 * there is no rail to hold it.
 */
export const WelcomeBanner = ({ profile, streak }) => {
  const momentum = momentumFor(streak);
  return (
    <div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-gradient-to-br tw-from-primary-solid tw-to-[color-mix(in_srgb,hsl(var(--primary-solid))_82%,black)] tw-p-4 tw-text-primary-solid-fg tw-shadow-card page-sm:tw-px-5 page-lg:tw-hidden">
      <div className="tw-flex tw-items-center tw-gap-3.5">
        <ProfileAvatar name={profile.name} src={profile.avatarUrl} className="tw-h-16 tw-w-16 page-md:tw-h-[72px] page-md:tw-w-[72px]" />
        <div className="tw-min-w-0 tw-flex-1">
          <h1 className="tw-m-0 tw-text-lg tw-font-medium tw-leading-tight tw-tracking-tight tw-text-primary-solid-fg page-sm:tw-text-xl">
            {greetingLine(profile.name)} <span aria-hidden="true">👋</span>
          </h1>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8125rem] tw-text-primary-solid-fg/90">Let&apos;s continue your learning journey</p>
          <div className="tw-mt-1.5 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
            {profile.active && (
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary-solid-fg/15 tw-px-2 tw-py-0.5 tw-text-[0.6875rem] tw-font-semibold">
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-success" aria-hidden="true" />
                Active
              </span>
            )}
            {profile.role && <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-primary-solid-fg/85">{profile.role}</span>}
          </div>
        </div>
        <div className="tw-hidden tw-max-w-[260px] tw-shrink-0 tw-items-center tw-gap-2.5 tw-rounded-md tw-bg-primary-solid-fg/15 tw-px-3 tw-py-2.5 page-md:tw-flex">
          <TrendingUp size={18} aria-hidden="true" className="tw-shrink-0" />
          <div className="tw-min-w-0">
            <div className="tw-text-[0.8125rem] tw-font-semibold tw-leading-tight">{momentum.title}</div>
            <div className="tw-mt-0.5 tw-text-xs tw-text-primary-solid-fg/85">{momentum.sub}</div>
          </div>
        </div>
      </div>
      <p className="tw-m-0 tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-primary-solid-fg/15 tw-px-3 tw-py-2 tw-text-xs page-md:tw-hidden">
        <TrendingUp size={14} aria-hidden="true" className="tw-shrink-0" />
        <span className="tw-min-w-0"><strong className="tw-font-semibold">{momentum.title}</strong> {momentum.sub}</span>
      </p>
    </div>
  );
};

/** From a laptop up the profile moves to the rail, and the greeting stands alone. */
export const WelcomeRow = ({ profile, streak }) => {
  const momentum = momentumFor(streak);
  return (
    <div className="tw-hidden tw-items-center tw-justify-between tw-gap-4 page-lg:tw-flex">
      <div className="tw-min-w-0">
        <h1 className="tw-m-0 tw-text-[1.625rem] tw-font-medium tw-leading-tight tw-tracking-tight tw-text-content">
          {greetingLine(profile.name)} <span aria-hidden="true">👋</span>
        </h1>
        <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-content-muted">Let&apos;s continue your learning journey</p>
      </div>
      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-rounded-lg tw-bg-primary-soft tw-px-4 tw-py-3">
        <span className="tw-flex tw-h-[38px] tw-w-[38px] tw-items-center tw-justify-center tw-rounded-md tw-bg-surface-raised tw-text-primary tw-shadow-card" aria-hidden="true">
          <TrendingUp size={18} />
        </span>
        <div>
          <div className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">{momentum.title}</div>
          <div className="tw-mt-0.5 tw-text-xs tw-text-content-muted">{momentum.sub}</div>
        </div>
      </div>
    </div>
  );
};
